import React from 'react';
import { clsx } from 'clsx';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { formatPercentage } from '@/utils/formatters';

interface SummaryCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeType?: 'percentage' | 'currency' | 'number';
  icon?: React.ComponentType<any>;
  trend?: 'up' | 'down' | 'neutral';
  className?: string;
  loading?: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  change,
  changeType = 'percentage',
  icon: Icon,
  trend,
  className,
  loading = false,
}) => {
  const getTrendIcon = () => {
    if (trend === 'up') return ChevronUpIcon;
    if (trend === 'down') return ChevronDownIcon;
    return null;
  };

  const TrendIcon = getTrendIcon();

  const formatChange = (changeValue: number) => {
    switch (changeType) {
      case 'percentage':
        return formatPercentage(changeValue);
      case 'currency':
        return `$${Math.abs(changeValue).toFixed(2)}`;
      case 'number':
        return changeValue.toFixed(2);
      default:
        return changeValue.toString();
    }
  };

  const getChangeText = (changeValue: number) => {
    const formattedChange = formatChange(Math.abs(changeValue));
    const sign = changeValue >= 0 ? '+' : '-';
    return `${sign}${formattedChange}`;
  };

  if (loading) {
    return (
      <div className={clsx('card', className)}>
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-3">
            <div className="h-4 bg-secondary-custom rounded w-24"></div>
            <div className="h-6 w-6 bg-secondary-custom rounded"></div>
          </div>
          <div className="h-8 bg-secondary-custom rounded w-32 mb-2"></div>
          <div className="h-4 bg-secondary-custom rounded w-20"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('card hover:shadow-professional transition-all duration-200', className)}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-caption text-secondary-custom truncate">{title}</h3>
        {Icon && (
          <div className="flex-shrink-0">
            <Icon className="h-6 w-6 text-blue-500" />
          </div>
        )}
      </div>
      
      <div className="flex items-end justify-between">
        <div>
          <p className="text-data-large text-primary-custom mb-2">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          
          {change !== undefined && (
            <div className="flex items-center">
              {TrendIcon && (
                <TrendIcon 
                  className={clsx(
                    'h-4 w-4 mr-1',
                    trend === 'up' ? 'text-bullish' : trend === 'down' ? 'text-bearish' : 'text-neutral'
                  )} 
                />
              )}
              <span className={clsx(
                'text-body font-medium',
                trend === 'up' ? 'text-bullish' : trend === 'down' ? 'text-bearish' : 'text-neutral'
              )}>
                {getChangeText(change)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryCard; 